import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import MainLayout from './layouts/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Core pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import JournalsPage from './pages/JournalsPage';
import JournalDetailPage from './pages/JournalDetailPage';
import SubmitPaperPage from './pages/SubmitPaperPage';
import EditorialBoardPage from './pages/EditorialBoardPage';
import PaperDetailPage from './pages/PaperDetailPage';

// Dashboards
import AuthorDashboard from './pages/AuthorDashboard';
import ReviewerDashboard from './pages/ReviewerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';

// About & Institute
import AboutPage from './pages/AboutPage';
import PeerReviewPage from './pages/PeerReviewPage';
import IndexingPage from './pages/IndexingPage';

// Submission Policies
import AuthorGuidelinesPage from './pages/AuthorGuidelinesPage';
import ManuscriptPreparationPage from './pages/ManuscriptPreparationPage';
import SubmissionProcessPage from './pages/SubmissionProcessPage';
import ReviewCriteriaPage from './pages/ReviewCriteriaPage';
import RevisionPolicyPage from './pages/RevisionPolicyPage';
import RejectionAppealsPage from './pages/RejectionAppealsPage';

// Publication Ethics
import PublicationEthicsPage from './pages/PublicationEthicsPage';
import COPEPage from './pages/COPEPage';
import ConflictOfInterestPage from './pages/ConflictOfInterestPage';
import PlagiarismPage from './pages/PlagiarismPage';
import OpenAccessPage from './pages/OpenAccessPage';
import CopyrightPage from './pages/CopyrightPage';
import EditorialPrinciplesPage from './pages/EditorialPrinciplesPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

import './App.css';

const DashboardRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const routes = {
    author: '/dashboard/author',
    reviewer: '/dashboard/reviewer',
    admin: '/dashboard/admin',
    superadmin: '/dashboard/superadmin',
  };
  return <Navigate to={routes[user.role] || '/login'} replace />;
};

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<MainLayout />}>
              {/* Core */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/journals" element={<JournalsPage />} />
              <Route path="/journals/:id" element={<JournalDetailPage />} />
              <Route path="/editorial-board" element={<EditorialBoardPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/dashboard" element={<DashboardRedirect />} />
              <Route path="/submit" element={<ProtectedRoute><SubmitPaperPage /></ProtectedRoute>} />
              <Route path="/papers/:id" element={<PaperDetailPage />} />

              {/* Dashboards */}
              <Route path="/dashboard/author" element={<ProtectedRoute role="author"><AuthorDashboard /></ProtectedRoute>} />
              <Route path="/dashboard/reviewer" element={<ProtectedRoute role="reviewer"><ReviewerDashboard /></ProtectedRoute>} />
              <Route path="/dashboard/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/dashboard/superadmin" element={<ProtectedRoute role="superadmin"><SuperAdminDashboard /></ProtectedRoute>} />

              {/* About & Institute */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/peer-review" element={<PeerReviewPage />} />
              <Route path="/indexing" element={<IndexingPage />} />

              {/* Submission Policies */}
              <Route path="/author-guidelines" element={<AuthorGuidelinesPage />} />
              <Route path="/manuscript-preparation" element={<ManuscriptPreparationPage />} />
              <Route path="/submission-process" element={<SubmissionProcessPage />} />
              <Route path="/review-criteria" element={<ReviewCriteriaPage />} />
              <Route path="/revision-policy" element={<RevisionPolicyPage />} />
              <Route path="/rejection-appeals" element={<RejectionAppealsPage />} />

              {/* Publication Ethics */}
              <Route path="/publication-ethics" element={<PublicationEthicsPage />} />
              <Route path="/cope-compliance" element={<COPEPage />} />
              <Route path="/conflict-of-interest" element={<ConflictOfInterestPage />} />
              <Route path="/plagiarism" element={<PlagiarismPage />} />
              <Route path="/open-access" element={<OpenAccessPage />} />
              <Route path="/copyright" element={<CopyrightPage />} />
              <Route path="/editorial-principles" element={<EditorialPrinciplesPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;
